import { CalendarRoot } from "PFApp/booking/types/calendar_root";
import { EditMode } from "PFApp/booking/types/edit_mode";
import { Row, RowType } from "PFApp/booking/types/row";
import { BookingForUpdate, BulkOperationFilter, Exclude } from "PFCore/services/bookings_bulk_operation";
import { BookingCategory, Id, RequirementType } from "PFTypes";

export type RowEventContext = {
  key: string;
  type: RowType;
  originalId: Id;
  parents: string[];
  parentIds: Id[];
  calendarRoot: CalendarRoot;
  bookingCategoryIds?: Id[];
  isLinkedToEngagement?: boolean;
  isAllBookingsModeEnabled?: boolean;
};

export type SelectionEvent =
  | {
      type: "SELECT_ROW";
      timestamp: number;
      row: RowEventContext;
      bookingsToReselect?: Id[];
    }
  | {
      type: "UNSELECT_ROW";
      timestamp: number;
      bookingsToDeselect?: Id[];
      isSelectAllMode?: boolean;
      row: RowEventContext;
    }
  | {
      type: "SELECT_BOOKING";
      timestamp: number;
      bookingId: Id;
      row: RowEventContext;
    }
  | {
      type: "UNSELECT_BOOKING";
      timestamp: number;
      bookingId: Id;
      row: RowEventContext;
    }
  | {
      type: "CLEAR_SELECTION";
      timestamp: number;
    };

export enum BulkAction {
  BulkUpdateDates = "BulkUpdateDates",
  BulkUpdateDetails = "BulkUpdateDetails",
  BulkUpdateDuration = "BulkUpdateDuration"
}

export enum BulkDatesMode {
  BulkMove = "BulkMove",
  BulkUpdateStartDate = "BulkUpdateStartDate",
  BulkUpdateEndDate = "BulkUpdateEndDate"
}

export type BulkDates = {
  mode: BulkDatesMode;
  date: string;
};

export type SelectModeData = {
  filters: BulkOperationFilter[];
  exclude?: Exclude;
};

export type BulkUpdateDetailOption<T> = {
  enabled: boolean;
  value: T;
};

export type BookingCategoryDetailsValue = { category: BookingCategory | null; updateBookingsTitles: boolean };

export type BulkUpdateDetails = {
  bookingCategoryId: BulkUpdateDetailOption<BookingCategoryDetailsValue>;
  title: BulkUpdateDetailOption<string>;
  description: BulkUpdateDetailOption<string>;
};

export type Duration = {
  durationType: RequirementType | null;
  durationValue: number | null;
};

export type ValuesToUpdate = {
  bulkAction: BulkAction;
  dates: BulkDates;
  details: BulkUpdateDetails;
  duration: Duration;
};

export type SelectModeSlice = {
  totalSelection: SelectModeData | null;
  isSelectAllMode: boolean;

  valuesToUpdate: ValuesToUpdate;

  bookingsCount: number | undefined;
  bookingsRangeMinDate: string | null;
  bookingsRangeMaxDate: string | null;
  selectableBookingIds: Id[];
  filteredBookings: BookingForUpdate[];
  filteredBookingsCategoriesIds: Id[];

  isLoading: boolean;
  isSubmitting: boolean;

  events: SelectionEvent[];
  selection: SelectModeData | null;
  selectedRows: Row[];
};

export type SplitAction = {
  bookingId: Id;
  splitDate: string;
};

export type SplitModeSlice = {
  bookingIds: Set<Id>;
  operationsHistory: SplitAction[];
  isLoading: boolean;
  isSubmitting: boolean;
};

export type WallPlannerModeSlice = {
  updatableBookingIds: Id[];
  isLoading: boolean;
  focusedInputId: string | null;
};

export type EditingCell = {
  rowKey: string;
  date: string;
};

export enum PendingEditStatus {
  Pending = "pending",
  Inflight = "inflight"
}

export type PendingEdit = {
  rowKey: string;
  date: string;
  activityId?: number;
  profileId: number;
  bookingCategoryId?: number;
  value: number;
  status: PendingEditStatus;
};

export type SmartPlannerModeSlice = {
  editingCell: EditingCell | null;
  updatableBookingIds: Id[];
  isLoading: boolean;
  pendingEdits: Record<string, PendingEdit>;
};

export type BookingEditModeStore = {
  editMode: EditMode;

  selectMode: SelectModeSlice;
  splitMode: SplitModeSlice;
  wallPlannerMode: WallPlannerModeSlice;
  smartPlannerMode: SmartPlannerModeSlice;

  actions: BookingEditModeStoreActions;
};

export type BookingEditModeStoreActions = {
  setEditMode: (mode: EditMode) => void;
  resetAll: () => void;

  selectMode: SelectModeActions;
  splitMode: SplitModeActions;
  wallPlannerMode: WallPlannerModeActions;
  smartPlannerMode: SmartPlannerModeActions;
};

export type SelectModeActions = {
  setTotalSelection: (selection: SelectModeData | null) => void;
  setIsSelectAllMode: (value: boolean) => void;
  setValuesToUpdate: (values: Partial<ValuesToUpdate>) => void;
  setBulkAction: (action: BulkAction) => void;
  changeDates: (dates: Partial<BulkDates>) => void;
  toggleDetailsOption: (key: keyof BulkUpdateDetails) => void;
  changeDetailsOptionValue: <K extends keyof BulkUpdateDetails>(
    key: K,
    value: BulkUpdateDetails[K]["value"]
  ) => void;
  changeDuration: (duration: Partial<Duration>) => void;
  resetValuesToUpdate: () => void;

  setMetadata: (metadata: {
    bookingsCount?: number;
    bookingsRangeMinDate?: string | null;
    bookingsRangeMaxDate?: string | null;
    selectableBookingIds?: Id[];
    filteredBookings?: BookingForUpdate[];
    filteredBookingsCategoriesIds?: Id[];
  }) => void;

  setIsLoading: (value: boolean) => void;
  setIsSubmitting: (value: boolean) => void;

  reset: () => void;

  addSelectionEvent: (event: SelectionEvent) => void;
};

export type SplitModeActions = {
  addBookingIds: (ids: Id[]) => void;
  splitBooking: (bookingId: Id, splitDate: string) => void;
  undoLastSplit: () => void;
  setIsLoading: (value: boolean) => void;
  setIsSubmitting: (value: boolean) => void;
  reset: () => void;
};

export type WallPlannerModeActions = {
  setUpdatableBookingIds: (ids: Id[]) => void;
  setIsLoading: (value: boolean) => void;
  reset: () => void;
  setFocusedInputId: (id: string | null) => void;
};

export type SmartPlannerOptimisticActions = {
  commitEdit: (key: string, edit: Omit<PendingEdit, "status">) => void;
  markInflight: (key: string) => void;
  clearEdit: (keys: string | string[]) => void;
  revertEdit: (key: string) => void;
};

export type SmartPlannerModeActions = {
  setEditingCell: (cell: EditingCell | null) => void;
  clearEditingCell: () => void;
  setUpdatableBookingIds: (ids: Id[]) => void;
  addUpdatableBookingIds: (ids: Id[]) => void;
  removeUpdatableBookingIds: (ids: Id[]) => void;
  setIsLoading: (value: boolean) => void;
  resetPendingEdits: () => void;
  reset: () => void;
} & SmartPlannerOptimisticActions;
